import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import TrianglesMiniSvg from '../other/svg/TrianglesMiniSvg'
import XMarkSvg from '../other/svg/XMarkSvg'
import { fieldBoxShadow } from './FormTextInput'

interface FormAutocompleteProps {
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder: string
  error?: boolean
  disabled?: boolean
}

interface MenuPosition {
  top: number
  left: number
  width: number
}

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const normalize = (value: string): string => value.replace(/\s/g, '').toUpperCase()

const FormAutocomplete = ({
  value,
  onChange,
  options,
  placeholder,
  error = false,
  disabled = false
}: FormAutocompleteProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const filteredOptions = useMemo(() => {
    if (value.length === 0) {
      return []
    }
    const query = normalize(value)
    return options.filter((option) => normalize(option).includes(query))
  }, [options, value])

  const showOptions = isOpen && !disabled && filteredOptions.length > 0

  useLayoutEffect(() => {
    const updatePosition = (): void => {
      if (wrapperRef.current === null) {
        return
      }
      const rect = wrapperRef.current.getBoundingClientRect()
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      })
    }

    if (showOptions) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
    } else {
      setMenuPosition(null)
    }

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [showOptions])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div className="self-stretch relative" ref={wrapperRef}>
      <div
        className={`self-stretch w-full h-11 pl-4 pr-1 bg-backgrounds-bg-field-component rounded-md inline-flex justify-center items-center overflow-hidden ${
          disabled && !error ? 'opacity-60' : ''
        }`}
        style={{ boxShadow: fieldBoxShadow(error) }}>
        <input
          type="text"
          value={value}
          disabled={disabled}
          autoComplete="off"
          onFocus={() => {
            setIsOpen(true)
          }}
          onChange={(event) => {
            onChange(event.target.value)
            setIsOpen(true)
          }}
          placeholder={placeholder}
          className="flex-1 h-full bg-transparent outline-none text-sm font-medium font-Montserrat leading-5 text-foregrounds-fg-base placeholder:text-foregrounds-fg-muted placeholder:font-medium"
        />
        {value.length > 0 && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              onChange('')
              setIsOpen(false)
            }}
            className="size-8 flex justify-center items-center shrink-0"
            aria-label="Clear">
            <XMarkSvg className="size-[15px] text-buttons-text-secondary" />
          </button>
        )}
        <div className="size-8 flex justify-center items-center overflow-hidden pointer-events-none shrink-0">
          <TrianglesMiniSvg className="size-[15px] text-foregrounds-fg-muted" />
        </div>
      </div>
      {showOptions &&
        menuPosition !== null &&
        createPortal(
          <>
            <div
              role="presentation"
              className="fixed inset-0 z-[100]"
              onMouseDown={(event) => {
                event.preventDefault()
                setIsOpen(false)
              }}
            />
            <div
              className="fixed z-[200] p-1 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                boxShadow: menuShadow
              }}>
              <div className="self-stretch max-h-52 overflow-y-auto flex flex-col justify-start items-start">
                {filteredOptions.map((option) => {
                  const isSelected = option === value
                  return (
                    <button
                      type="button"
                      key={option}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        onChange(option)
                        setIsOpen(false)
                      }}
                      className="self-stretch relative pl-9 pr-3 py-2 rounded-sm inline-flex justify-start items-center gap-2 bg-backgrounds-bg-field-component hover:bg-backgrounds-bg-component-hover">
                      {isSelected && (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="absolute left-3 size-3.5 text-foregrounds-fg-base">
                          <path
                            d="M3 7.2L5.7 10L11 4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      <div className="flex-1 text-left text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5">
                        {option}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

export default FormAutocomplete
